import { useState, useEffect } from "react";
import "./common.css";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);

const API_BASE = "https://atmsim-pip.onrender.com/api";

const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000];

export default function UpiPage() {
  const params = new URLSearchParams(window.location.search);
  const sessionAmt = params.get("amount");

  const [step, setStep] = useState("home");
  const [amount, setAmount] = useState(sessionAmt || "");
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE}/dashboard`, {
        headers: {
          Authorization: token,
        },
      });

      const data = await res.json();

      setBalance(data.balance || 0);
      setHistory(data.analytics || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleWithdraw = () => {
    const amt = Number(amount);

    if (!amt || amt <= 0) {
      setError("Enter valid amount");
      return;
    }

    if (amt > balance) {
      setError("Insufficient balance");
      return;
    }

    if (amt % 100 !== 0) {
      setError("Amount must be multiple of ₹100");
      return;
    }

    setError("");
    setStep("confirm");
  };

  const confirmWithdraw = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE}/withdraw`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          amount: Number(amount),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Withdraw failed");
      }

      setBalance(data.new_balance);

      setHistory((prev) => [
        {
          date: new Date().toLocaleString(),
          amount: Number(amount),
          type: "withdraw",
        },
        ...prev,
      ]);

      setStep("success");
    } catch (err) {
      setError(err.message);
      setStep("amount");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setAmount("");
    setError("");
    setStep("home");
  };

  return (
    <div className="upi-page">
      <div className="upi-header">
        <div>
          <h2>NAPPS Pay</h2>
          <p>UPI ATM Withdrawal</p>
        </div>
      </div>

      <div className="upi-balance-card">
        <div>Available Balance</div>
        <h1>{fmtINR(balance)}</h1>
      </div>

      {step === "home" && (
        <>
          <div className="upi-actions">
            <button
              className="upi-primary-btn"
              onClick={() => setStep("amount")}
            >
              Withdraw Cash
            </button>
          </div>

          <h3>Quick Withdraw</h3>

          <div className="upi-quick-grid">
            {QUICK_AMOUNTS.map((v) => (
              <button
                key={v}
                className="upi-quick-chip"
                onClick={() => {
                  setAmount(String(v));
                  setStep("confirm");
                }}
              >
                ₹{v}
              </button>
            ))}
          </div>

          <h3 style={{ marginTop: 25 }}>
            Recent Transactions
          </h3>

          <div className="upi-history">
            {history.length === 0 && (
              <p>No transactions yet</p>
            )}

            {history.map((item, index) => (
              <div
                className="upi-history-row"
                key={index}
              >
                <div>
                  {item.type || "withdraw"}
                </div>

                <div>
                  ₹{Number(item.amount).toLocaleString(
                    "en-IN"
                  )}
                </div>

                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.7,
                  }}
                >
                  {item.date}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {step === "amount" && (
        <div className="upi-form">
          <h2>Enter Amount</h2>

          <input
            type="number"
            className="upi-amount-input"
            placeholder="Enter amount"
            value={amount}
            onChange={(e) =>
              setAmount(e.target.value)
            }
          />

          {error && (
            <div className="upi-error">
              {error}
            </div>
          )}

          <button
            className="upi-primary-btn"
            onClick={handleWithdraw}
          >
            Continue
          </button>

          <button
            className="upi-ghost-btn"
            onClick={reset}
          >
            Cancel
          </button>
        </div>
      )}

      {step === "confirm" && (
        <div className="upi-form">
          <h2>Confirm Withdrawal</h2>

          <h1>{fmtINR(Number(amount))}</h1>

          <div className="upi-confirm-card">
            <p>Account: NAPPS Bank</p>

            <p>
              Balance After Withdrawal:
              {" "}
              {fmtINR(
                balance - Number(amount)
              )}
            </p>
          </div>

          <button
            className="upi-primary-btn"
            disabled={loading}
            onClick={confirmWithdraw}
          >
            {loading
              ? "Processing..."
              : "Confirm Withdraw"}
          </button>

          <button
            className="upi-ghost-btn"
            onClick={() => setStep("amount")}
          >
            Back
          </button>
        </div>
      )}

      {step === "success" && (
        <div className="upi-success">
          <h1>✅</h1>

          <h2>Withdrawal Successful</h2>

          <h1>{fmtINR(Number(amount))}</h1>

          <p>
            Collect cash from ATM dispenser
          </p>

          <p>
            New Balance:
            {" "}
            {fmtINR(balance)}
          </p>

          <button
            className="upi-primary-btn"
            onClick={reset}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}